import React, { useEffect, useState } from 'react';
import authApi, { AnnouncementResponse, AnnouncementTag } from '../../services/authApi';
import { useBadge } from '../../context/BadgeContext';
import PageGate from '../PageGate';
import './tool-page.css';
import './CoursesPage.css';
import './AnnouncementsPage.css';

const TAG_LABELS: Record<AnnouncementTag, string> = {
  INFO:      'Info',
  IMPORTANT: 'Important',
  WARNING:   'Warning',
};

const AnnouncementsPage: React.FC = () => {
  const [items, setItems]         = useState<AnnouncementResponse[]>([]);
  const [loading, setLoading]     = useState(true);
  const [activeTag, setActiveTag] = useState<'All' | AnnouncementTag>('All');
  const { markSeen } = useBadge();

  useEffect(() => { markSeen('UPDATES'); }, [markSeen]);

  useEffect(() => {
    authApi.getAnnouncements()
      .then((r) => setItems(r.data ?? []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const tags: ('All' | AnnouncementTag)[] = ['All', 'INFO', 'IMPORTANT', 'WARNING'];
  const visible = activeTag === 'All' ? items : items.filter((i) => i.tag === activeTag);
  const pinnedCount = items.filter((i) => i.isPinned).length;

  return (
    <PageGate requirePremium>
    <div className="tool-page">
      <div className="courses-hero">
        <p className="courses-eyebrow">Platform News</p>
        <h1 className="courses-title">Updates</h1>
        <p className="courses-subtitle">Learning releases, platform improvements, and important notices.</p>
        <div className="courses-stats">
          <div className="cstat-card">
            <span className="cstat-num">{items.length}</span>
            <span className="cstat-label">Updates</span>
          </div>
          <div className="cstat-card cstat-card--free">
            <span className="cstat-num">{pinnedCount}</span>
            <span className="cstat-label">Pinned</span>
          </div>
        </div>
      </div>

      <div className="courses-toolbar">
        {tags.map((t) => (
          <button
            key={t}
            className={`courses-filter-btn ${activeTag === t ? 'active' : ''}`}
            onClick={() => setActiveTag(t)}
          >
            {t === 'All' ? 'All' : TAG_LABELS[t]}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="ann-list">
          {[1,2,3].map((i) => <div key={i} className="course-skeleton" style={{ height: 110 }} />)}
        </div>
      ) : visible.length === 0 ? (
        <div className="empty-state">No announcements yet.</div>
      ) : (
        <div className="ann-list">
          {visible.map((a) => (
            <div key={a.id} className={`ann-card ${a.isPinned ? 'ann-card--pinned' : ''}`}>
              <div className="ann-card-head">
                <div className="ann-title-row">
                  {a.isPinned && <span className="ann-pin">📌</span>}
                  <span className="ann-title">{a.title}</span>
                </div>
                <div className="ann-meta">
                  <span className={`ann-tag ann-tag--${a.tag.toLowerCase()}`}>{TAG_LABELS[a.tag]}</span>
                  <span className="ann-date">{formatDate(a.createdAt)}</span>
                </div>
              </div>
              <div className="ann-content" dangerouslySetInnerHTML={{ __html: a.content }} />
              {a.videoUrl && (
                <div className="ann-video-wrap">
                  <video src={a.videoUrl} controls className="ann-video" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
    </PageGate>
  );
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

export default AnnouncementsPage;
